import styled, { keyframes } from "styled-components/macro";
import React, { useEffect, useMemo, useState } from "react";
import { _project } from "state/store.projects";
import { get, getFocus, useGet } from "state/jotai";
import { Link, useLocation } from "react-router-dom";
import { active_project } from "state/store.global";
import { getProjectData } from "state/jotai.projectDataHooks";
import { Icon } from "assets/Icons_";
import { BREAK_ } from "constants";
import { useBreak } from "hooks/useBreak";
import { useTranslation } from "react-i18next";

const Header = ({ page, HeroSection }) => {
  const downloads = getProjectData("downloads", 0, []);
  const {
    i18n: { language },
  } = useTranslation();
  const { pin, pid } = useGet(active_project);
  const isMobile = useBreak("md_dn");
  const menuItem = HeroSection?.MenuItem;

  const gallery = get(_project, "gallery");
  const listOfPhotos =
    Object.values(gallery || {})
      .map((entry) => entry?.Data?.ListOfPhotos)
      .find(Boolean) || [];

  const fallbackImages = listOfPhotos
    .filter((photo) => photo?.UrlOriginal)
    .slice(0, 3);

  const randomImage = useMemo(() => {
    return fallbackImages[Math.floor(Math.random() * fallbackImages.length)]
      ?.UrlOriginal;
  }, [fallbackImages]);

  const backgroundUrl = useMemo(() => {
    if (page === "frontpage") {
      return HeroSection?.HeroImage?.[0];
    } else if (page === "unitpage" || page === "propertypage") {
      return HeroSection?.HeroImages?.[0];
    } else {
      return randomImage;
    }
  }, [page, fallbackImages, randomImage, HeroSection]);

  return (
    <Container style={{ backgroundImage: `url(${backgroundUrl})` }}>
      {page === "frontpage" && (
        <Content>
          {/* ✅ Logo removed entirely — no element rendered */}

          <Menuwrap>
            <Menus>
              {menuItem?.map((item, idx) => {
                const href = `/${language}${item?.link}`;
                return (
                  <Link
                    to={href}
                    key={idx}
                    style={{
                      textDecoration: "none",
                      color: "inherit",
                    }}
                  >
                    <MenuItem>
                      <Icon
                        icon={item.icon}
                        isFaIcon={false}
                        size={isMobile ? 22 : 24}
                        color={"#000"}
                      />
                      <Text className={isMobile ? "h5" : "h3"} clr={"#000"}>
                        {item.label}
                      </Text>
                    </MenuItem>
                  </Link>
                );
              })}
            </Menus>
          </Menuwrap>
        </Content>
      )}
    </Container>
  );
};

export default Header;

// ANIMATION
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// STYLED COMPONENTS

const Container = styled.div`
  position: relative;
  height: 539px;
  width: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  ${BREAK_.md_dn} {
    height: 250px;
  }
`;

const Menuwrap = styled.div`
  width: 100%;
  padding: 0 16px;

  @media (min-width: 768px) {
    padding: 0 16px;
  }
  ${BREAK_.md_up} {
    padding: 0;
  }
`;

const Content = styled.div`
  position: absolute;
  top: 50%; /* ✅ center vertically */
  left: 50%;
  transform: translate(-50%, -50%); /* ✅ perfect centering */
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  ${BREAK_.md_dn} {
    top: 50%;
    gap: 16px;
    width: 100%;
  }
`;

const Menus = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  width: 100%;
  overflow-x: auto;
  white-space: nowrap;
  border-radius: 40px;
  background: var(--ei-do-white-90, rgba(255, 255, 255, 0.9));
  padding: 8px 16px;
  scrollbar-width: none;
  -ms-overflow-style: auto;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 740px) and (max-width: 1100px) {
    padding: 13px 80px;
    justify-content: space-between;
  }

  @media (min-width: 1100px) {
    justify-content: center;
    padding: 13px 80px;
    gap: 144px;
  }
`;

export const Text = styled.div`
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.3s ease;
`;

const MenuItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 60px;
  flex-shrink: 0;
  cursor: pointer;
  animation: ${fadeInUp} 0.4s ease forwards;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.07);
    svg {
      transform: scale(1.1);
    }
  }

  svg {
    transition: transform 0.3s ease;
  }
`;
